import React, { useRef, useEffect } from 'react';

import { forEach, isArray, isObject, camelCase, snakeCase, capitalize, isEmpty } from 'lodash';

import {
  INPUT_VALUE_DATEFORMAT,
  atsList,
  displayDateReportFormat,
  displayMonthAndYearFormat,
  hrmsList
} from './CommonConstants';
import { toast } from 'react-toastify';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import AppConstants from '/src/core-components/AppConstants';
import moment from 'moment';
import Image from '../core-components/Image/Image';
import { ADMIN_URL } from '../core-components/Utilities/CommonConstants';
import { integrationsCardData } from '../Integrations/integrationItems';

export const getTrimmedValue = (value, withSpace) => {
  if (value) {
    return withSpace ? value.trim().replace(/\s\s+/g, ' ') : value.trim().replace(/\s+/g, '');
  } else {
    return '';
  }
};

export const capitalizeName = (value) => {
  if (value) {
    return value.replace(/\w+/g, (word) => {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }).replace(/'[a-zA-Z]/g, (match) => {
      return match.charAt(0) + match.charAt(1).toLowerCase();
    });
  }
  return '';
};

//This capitalises the name and keeps letter after apostrophe unchanged.
export const eduCapitalizeName = (value) => {
  return value.replace(/\b\w+('\w+)?\b/g, (match) => {
    if (match.includes("'")) {
      return match.charAt(0).toUpperCase() + match.slice(1);
    }
    return match.charAt(0).toUpperCase() + match.slice(1).toLowerCase();
  });
};

// To get consistent ID check names - PAN, Aadhaar, Passport, Voter ID, Driving License
export const getConsistentIdCheckNames = (idType) => {
  if (typeof idType !== 'string') {
    return idType;
  }
  let newIdType = idType;
  const lowerCaseIdType = idType.toLowerCase();
  const idMapper = {
    Aadhaar: ['aadhaar', 'aadhaar card'],
    PAN: ['pan', 'pan card', 'pan number'],
    'Driving License': ['driving license', 'driving licence'],
    'Voter ID': ['voter id'],
    Passport: ['passport']
  };
  Object.keys(idMapper).some((key) => {
    if (idMapper[key].includes(lowerCaseIdType)) {
      newIdType = key;
      return true;
    }
    return false;
  });
  return newIdType;
};

export const getErrorMessage = (error) => {
  if (error?.response?.data || error?.data || (error?.errors && error?.errors?.length)) {
    const errorObject =
      (error?.data && error?.data?.errorMessage) ||
      error?.response?.data?.message ||
      error?.response?.data?.errorMsg ||
      (Array.isArray(error?.response?.data?.errors) &&
        error?.response?.data?.errors?.length &&
        error?.response?.data?.errors[0]?.msg) ||
      error?.response?.data?.msg ||
      error?.response?.data ||
      error?.errors[0]?.msg ||
      'Something went wrong.';

    switch (typeof errorObject) {
      case 'object':
        return (
          errorObject?.msg ||
          JSON.stringify(errorObject.msg) ||
          errorObject?.data?.msg ||
          errorObject?.resp?.msg
        );
      case 'string':
        return errorObject;
      default:
        return 'Something went wrong.';
    }
  } else {
    if (typeof error === 'string') {
      return error;
    }
  }
};

// Toast Error with ID to avoid duplicate messages

export const getMessageIdForToast = (message) => {
  if (message) return message.replace(/ +/g, '_').toLowerCase();
  return '';
};

export const errToastMessage = (err) => {
  const message = getErrorMessage(err);

  if (message) {
    const id = getMessageIdForToast(message);
    toast.error(message, { toastId: id });
  }
};

export const toSnakeCase = (camel_case_object) => {
  var snakeCaseObject = {};
  forEach(camel_case_object, function (value, key) {
    if (isArray(value)) {
      value = value.map((v) => {
        return isObject(v) ? toSnakeCase(v) : v;
      });
    } else if (isObject(value)) {
      value = toSnakeCase(value);
    }
    snakeCaseObject[snakeCase(key)] = value;
  });
  return snakeCaseObject;
};

export const toCamelCase = (snake_case_object) => {
  let camelCaseObject = {};
  forEach(snake_case_object, function (value, key) {
    if (isArray(value)) {
      value = value.map((v) => {
        return isObject(v) ? toCamelCase(v) : v;
      });
    } else if (isObject(value)) {
      value = toCamelCase(value);
    }
    camelCaseObject[camelCase(key)] = value;
  });
  return camelCaseObject;
};

export const copyToClipboard = (text, message) => {
  const type = 'text/plain';
  const blob = new Blob([text], { type });
  if (!navigator?.clipboard || !navigator?.clipboard?.write) {
    // Clipboard API not supported in this browser
    errToastMessage('Clipboard API is not supported in this browser!');
    return;
  }
  const data = [new ClipboardItem({ [type]: blob })];
  try {
    navigator?.clipboard.write(data).then(() => {
      toast.info(message);
    });
  } catch (error) {
    errToastMessage('Copying was unsuccessful!');
  }
};

/**
 * Validates phone number
 * case 1: if input given contains intl number(eg:+15417543010),checks for validity
 * case 2: if local string(eg:9892782982) is given parses the number with default code(i.e india) and checks validity
 * @param   {string} phoneNumber
 * @return  {boolean}
 */
export const isValidPhoneNumber = (value) => {
  const regexForTenDigit = /^[6-9]{1}[0-9]{9}/;
  const regexForAllowedCharacter = /^[0-9+]+$/;
  try {
    let parsedPhone = null;
    let country;
    let phone = value.toString();

    // Numbers without country codes ( assuming indian numbers )
    if (phone[0] !== '+' && Number.isInteger(parseInt(phone[0], 10))) {
      if (phone.length === 10 && phone.match(regexForTenDigit)) {
        phone = `+91${phone}`;
      } else if (phone[0] === '0' && phone.slice(1).match(regexForTenDigit)) {
        phone = `+91${phone.slice(1)}`;
      } else {
        return false;
      }
    }
    if (phone.slice(0, 3) === '+91') {
      // library was allowing indian numbers with less than 10 digits
      if (!phone.slice(3).match(regexForTenDigit)) {
        return false;
      }
      country = 'IN';
    }

    if (!phone.match(regexForAllowedCharacter)) {
      return false;
    }
    // parse number ( parses numbers in specific formats also e.g. (+91) 123 456 7890 )
    // also parses numbers with different country codes
    parsedPhone = parsePhoneNumberFromString(phone, country);

    // validate the parsed number
    if (parsedPhone == undefined || (parsedPhone && !parsedPhone.isValid())) {
      return false;
    }

    return true;
  } catch (err) {
    // try catch block was required because parsePhoneNumber throws an error if number is invalid
    return false;
  }
};

export const jwtParser = (jwtToken) => {
  try {
    return JSON.parse(atob(jwtToken.split('.')[1]));
  } catch (e) {
    return null;
  }
};

export const getCustomCheckSVG = (
  color = '#999999',
  width = '35px',
  height = '18px',
  cursor = 'default'
) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox='0 0 94 94'
      fill={'none'}
      style={{ display: 'inline-block', verticalAlign: 'middle', cursor: cursor }}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M15.1254 7.21425C15.0941 6.92665 14.9578 6.66073 14.7424 6.46755C14.5271 6.27436 14.248 6.16754 13.9587 6.16758H13.5387C13.4536 5.89611 13.3349 5.63633 13.1854 5.39425L13.5054 5.06758C13.7111 4.86311 13.8332 4.58935 13.848 4.2997C13.8628 4.01005 13.7692 3.72528 13.5854 3.50092C13.2598 3.09535 12.8909 2.72651 12.4854 2.40092C12.2599 2.21668 11.9741 2.12283 11.6833 2.13761C11.3925 2.1524 11.1176 2.27476 10.912 2.48092L10.6254 2.78758C10.3763 2.65084 10.1093 2.54985 9.83204 2.48758V2.04092C9.83359 1.75297 9.72798 1.47474 9.53576 1.26034C9.34354 1.04594 9.07845 0.910701 8.79204 0.880918C8.26708 0.820944 7.737 0.820944 7.21204 0.880918C6.92444 0.909149 6.65772 1.04372 6.46413 1.25828C6.27053 1.47284 6.16399 1.75193 6.16538 2.04092V2.46092C5.89341 2.54479 5.63347 2.66355 5.39204 2.81425L5.07204 2.49425C4.86646 2.28809 4.59157 2.16573 4.3008 2.15095C4.01003 2.13616 3.72414 2.23001 3.49871 2.41425C3.09781 2.74058 2.73346 3.1094 2.41204 3.51425C2.22826 3.73861 2.13463 4.02339 2.14943 4.31304C2.16422 4.60269 2.28636 4.87644 2.49204 5.08092L2.79871 5.37425C2.6641 5.62428 2.56322 5.89105 2.49871 6.16758H2.03871C1.74942 6.16754 1.47031 6.27436 1.25498 6.46755C1.03965 6.66073 0.903273 6.92665 0.872043 7.21425C0.818694 7.73734 0.818694 8.2645 0.872043 8.78758C0.903273 9.07518 1.03965 9.34111 1.25498 9.53429C1.47031 9.72747 1.74942 9.8343 2.03871 9.83425H2.45871C2.54517 10.1052 2.66379 10.3648 2.81204 10.6076L2.49204 10.9343C2.28636 11.1387 2.16422 11.4125 2.14943 11.7021C2.13463 11.9918 2.22826 12.2766 2.41204 12.5009C2.73763 12.9065 3.10648 13.2753 3.51204 13.6009C3.73748 13.7852 4.02336 13.879 4.31413 13.8642C4.6049 13.8494 4.87979 13.7271 5.08538 13.5209L5.37204 13.2143C5.6211 13.351 5.88816 13.452 6.16538 13.5143V13.9743C6.16383 14.2622 6.26944 14.5404 6.46166 14.7548C6.65388 14.9692 6.91897 15.1045 7.20538 15.1343C7.46857 15.1666 7.73354 15.1822 7.99871 15.1809C8.26166 15.1824 8.52444 15.1668 8.78538 15.1343C9.07298 15.106 9.3397 14.9715 9.53329 14.7569C9.72689 14.5423 9.83343 14.2632 9.83204 13.9743V13.5543C10.104 13.4704 10.3639 13.3516 10.6054 13.2009L10.9254 13.5209C11.131 13.7271 11.4059 13.8494 11.6966 13.8642C11.9874 13.879 12.2733 13.7852 12.4987 13.6009C12.9059 13.2771 13.2749 12.9081 13.5987 12.5009C13.7819 12.2767 13.8758 11.9927 13.8623 11.7035C13.8487 11.4143 13.7287 11.1403 13.5254 10.9343L13.212 10.6409C13.3488 10.3919 13.4498 10.1248 13.512 9.84758H13.972C14.2613 9.84763 14.5404 9.74081 14.7558 9.54762C14.9711 9.35444 15.1075 9.08852 15.1387 8.80092C15.1921 8.27783 15.1921 7.75067 15.1387 7.22758L15.1254 7.21425ZM10.6654 10.5009H7.16538V11.0009C7.16538 11.1335 7.1127 11.2607 7.01893 11.3545C6.92516 11.4482 6.79798 11.5009 6.66538 11.5009C6.53277 11.5009 6.40559 11.4482 6.31182 11.3545C6.21805 11.2607 6.16538 11.1335 6.16538 11.0009V10.5009H5.33204C5.19943 10.5009 5.07226 10.4482 4.97849 10.3545C4.88472 10.2607 4.83204 10.1335 4.83204 10.0009C4.83204 9.86831 4.88472 9.74113 4.97849 9.64736C5.07226 9.5536 5.19943 9.50092 5.33204 9.50092H6.16538V9.00092C6.16538 8.86831 6.21805 8.74113 6.31182 8.64736C6.40559 8.5536 6.53277 8.50092 6.66538 8.50092C6.79798 8.50092 6.92516 8.5536 7.01893 8.64736C7.1127 8.74113 7.16538 8.86831 7.16538 9.00092V9.50092H10.6654C10.798 9.50092 10.9252 9.5536 11.0189 9.64736C11.1127 9.74113 11.1654 9.86831 11.1654 10.0009C11.1654 10.1335 11.1127 10.2607 11.0189 10.3545C10.9252 10.4482 10.798 10.5009 10.6654 10.5009ZM10.6654 6.50092H9.83204V7.00092C9.83204 7.13353 9.77936 7.2607 9.6856 7.35447C9.59183 7.44824 9.46465 7.50092 9.33204 7.50092C9.19943 7.50092 9.07226 7.44824 8.97849 7.35447C8.88472 7.2607 8.83204 7.13353 8.83204 7.00092V6.50092H5.33204C5.19943 6.50092 5.07226 6.44824 4.97849 6.35447C4.88472 6.2607 4.83204 6.13353 4.83204 6.00092C4.83204 5.86831 4.88472 5.74113 4.97849 5.64736C5.07226 5.5536 5.19943 5.50092 5.33204 5.50092H8.83204V5.00092C8.83204 4.86831 8.88472 4.74113 8.97849 4.64736C9.07226 4.5536 9.19943 4.50092 9.33204 4.50092C9.46465 4.50092 9.59183 4.5536 9.6856 4.64736C9.77936 4.74113 9.83204 4.86831 9.83204 5.00092V5.50092H10.6654C10.798 5.50092 10.9252 5.5536 11.0189 5.64736C11.1127 5.74113 11.1654 5.86831 11.1654 6.00092C11.1654 6.13353 11.1127 6.2607 11.0189 6.35447C10.9252 6.44824 10.798 6.50092 10.6654 6.50092Z'
        fill={color}
      />
    </svg>
  );
};

export const getActiveCheckboxIcon = () => {
  return <Image name='ActiveCheckbox.svg' />;
};

export const getInActiveCheckboxIcon = () => {
  return <Image name='InActiveCheckbox.svg' />;
};

export const getActiveIcon = (type, state) => {
  switch (type) {
    case 'identity':
      return state === 'active' ? (
        <Image name='ActiveIdentity.svg' />
      ) : (
        <Image name='InActiveIdentity.svg' />
      );
    case 'court':
      return state === 'active' ? (
        <Image name='ActiveCourt.svg' />
      ) : (
        <Image name='InActiveCourt.svg' />
      );
    case 'address':
      return state === 'active' ? (
        <Image name='ActiveAddress.svg' />
      ) : (
        <Image name='InActiveAddress.svg' />
      );
    case 'employment':
      return state === 'active' ? (
        <Image name='ActiveEmployment.svg' />
      ) : (
        <Image name='InActiveEmployment.svg' />
      );
    case 'education':
      return state === 'active' ? (
        <Image name='ActiveEducation.svg' />
      ) : (
        <Image name='InActiveEducation.svg' />
      );
    case 'worldCheck':
      return state === 'active' ? (
        <Image name='ActiveWorld.svg' />
      ) : (
        <Image name='InActiveWorld.svg' />
      );
    case 'cibilCheck':
      return state === 'active' ? (
        <Image name='ActiveCibilCheck.svg' />
      ) : (
        <Image name='InActiveCibilCheck.svg' />
      );
    case 'drugCheck':
      return state === 'active' ? (
        <Image name='ActiveDrugCheck.svg' />
      ) : (
        <Image name='InActiveDrugCheck.svg' />
      );
    case 'referenceCheck':
      return state === 'active' ? (
        <Image name='ActiveReferenceCheck.svg' />
      ) : (
        <Image name='InActiveReferenceCheck.svg' />
      );
    case 'customCheck':
      return state === 'active' ? (
        getCustomCheckSVG('#5989f0', 20, 20)
      ) : (
        <Image name='InActiveCustom.svg' />
      );
    default:
      return null;
  }
};

export const getOverallStatusSVG = (status) => {
  let colorData = {
    // 3: { status: 'AwaitingInput', color: '#c2c2c2', label: 'Interim Report' ,x:'15', y:'13' ,width:'150'},
    11: {
      status: 'ConsentMissing',
      color: '#FF8800',
      label: 'Interim Report',
      x: '15',
      y: '13',
      width: '100'
    },
    4: {
      status: 'FormSubmitted',
      color: '#0099cc',
      label: 'Interim Report',
      x: '15',
      y: '13',
      width: '100'
    },
    0: {
      status: 'InProgress',
      color: '#0d47a1',
      label: 'Interim Report',
      x: '15',
      y: '13',
      width: '100'
    },
    1: { status: 'Verified', color: '#21d549', label: 'Completed', x: '17', y: '13', width: '90' },
    6: {
      status: 'CompletedWithException',
      color: '#ff8800',
      label: 'Completed With Exception',
      x: '12',
      y: '13',
      width: '150'
    },
    9: {
      status: 'OnHold',
      color: '#979797',
      label: 'Interim Report',
      x: '15',
      y: '13',
      width: '100'
    },
    10: {
      status: 'Cancelled',
      color: '#979797',
      label: 'Cancelled',
      x: '25',
      y: '13',
      width: '100'
    },
    8: { status: 'Closed', color: '#979797', label: 'Closed', x: '32', y: '13', width: '100' },
    12: {
      status: 'InsufficientFunds',
      color: '#979797',
      label: 'Interim Report',
      x: '15',
      y: '13',
      width: '100'
    }
  };
  let colorOptions = colorData[status] || null;
  return (
    <>
      {colorOptions ? (
        <svg
          width='160'
          height='21'
          viewBox='0 0 150 21'
          version='1.1'
          xmlns='http://www.w3.org/2000/svg'
        >
          <title>Group 9</title>
          <desc>Created with Sketch.</desc>
          <g id='30-July' stroke='none' strokeWidth='1' fill='none' fillRule='evenodd'>
            <g id='CheckList_Icons_Final' transform='translate(-432, -683.000000)'>
              <g id='AwaitingInput' transform='translate(427.000000, 683.102273)'>
                <g id='Group-9'>
                  <rect width={colorOptions.width} height='20' rx='10' fill={colorOptions.color} />
                  <text
                    id='Awaiting-Input'
                    fontFamily='Montserrat, sans-serif'
                    fontSize='9'
                    fontWeight='600'
                    fill='#FFFFFF'
                  >
                    <tspan x={colorOptions.x} y={colorOptions.y}>
                      {colorOptions.label}
                    </tspan>
                  </text>
                </g>
              </g>
            </g>
          </g>
        </svg>
      ) : null}
    </>
  );
};

export const getCityNameWithoutAsterisk = (data) => {
  if (!isEmpty(data) && data.endsWith('*')) {
    return data.slice(0, -1);
  }
  return data;
};

export const capitalizeCase = (letter) => {
  return letter[0]?.toUpperCase() + letter.slice(1).toLowerCase();
};

export const getDocPipingUrl = (uuid, candidateId) => {
  return `${AppConstants.baseURL}candidate/document?uuid=${uuid}&candidate_id=${candidateId}`;
};

export const getDocPipingUrlCA = (uuid, checkType, candidateId) => {
  const isSA = ADMIN_URL.some((str) => AppConstants.baseURL.toString().includes(str));
  return `${
    isSA ? AppConstants.caBaseURL : AppConstants.baseURL
  }candidate/document?uuid=${uuid}&candidate_id=${candidateId}&checkType=${checkType}`;
};

export const isRegularDate = (date) => {
  return moment(date, INPUT_VALUE_DATEFORMAT, true).isValid();
};

export const removeAsterisks = (str) => {
  return !isEmpty(str) ? str.replace(/\*/g, '') : '';
};

export const getFormattedDate = (date, isReport = false, MYformat = false) => {
  let formattedDate = '';
  if (moment(date, ['MMM YYYY', 'MMMM YYYY', 'YYYY', 'YYYY-MM-DD', 'YYYY-MM'], true).isValid()) {
    if (isReport && MYformat && moment(date, ['YYYY-MM-DD', 'YYYY-MM'], true).isValid()) {
      formattedDate = moment(date).format(displayMonthAndYearFormat);
      return formattedDate;
    }
    if (moment(date, 'YYYY-MM-DD', true).isValid()) {
      formattedDate = moment(date).format(isReport ? displayDateReportFormat : 'YYYY-MM-DD');
    } else if (moment(date, 'MMM YYYY', true).isValid()) {
      formattedDate = moment(date).format('MMM YYYY');
    } else if (moment(date, 'MMMM YYYY', true).isValid()) {
      formattedDate = moment(date).format('MMMM YYYY');
    } else if (moment(date, 'YYYY', true).isValid()) {
      formattedDate = moment(date).format('YYYY');
    } else if (moment(date, 'YYYY-MM', true).isValid()) {
      formattedDate = moment(date).format(isReport ? displayMonthAndYearFormat : 'YYYY-MM');
    }
  }
  return formattedDate;
};

// Covert Blob to Pdf and download
export const generatePdf = (data, fileName, setState = null) => {
  try {
    const pdfBlob = new Blob([data]);
    const dataURL = URL.createObjectURL(pdfBlob);
    const downloadLink = document.createElement('a');
    downloadLink.href = dataURL;
    downloadLink.download = (fileName || 'Candidate') + '_Report.pdf';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(dataURL);
    toast.success('Report downloaded successfully.');
    if (setState) setState(false);
  } catch (err) {
    console.error('Generate PDF Error:::', err);
    if (setState) setState(false);
  }
};

// Covert to comma separated string
export const convertToCommaSeparateString = (value) => {
  if (value) {
    return parseInt(value.replace(/,/g, '')).toLocaleString('en-IN');
  } else {
    return '';
  }
};

/**
 *  Function ot get label by value
 * @param {String} value
 * @param {Object} obj
 * @returns
 */
export const getLabelByValue = (value, obj) => {
  const val = obj.find((course) => course.value === value);
  return val ? val.label : '';
};

/**
 * Processes the HRMS ATS options based on the provided list and integration data.
 *
 * @param {Array} list - The list of HRMS ATS options.
 * @param {Object} integrationData - The integration data object.
 * @returns {Array} - The processed options array.
 */
export const processHrmsAtsOptions = (list, integrationData, isAtsList) => {
  const options = [];

  if (!list) return options;

  list?.forEach((item) => {
    let integrationName = item?.trim();
    let integrationOption = null;

    if (
      integrationName === 'GreytHR' &&
      integrationData?.connectedIntegrations?.includes('Repute')
    ) {
      integrationOption = {
        value: integrationName,
        label: integrationName,
        status: 'connected',
        url: ''
      };
    } else if (integrationName?.toLowerCase() === 'repute') {
      integrationOption = {
        value: integrationName,
        label: integrationName,
        status: 'not-connected-unavailable',
        url: ''
      };
    } else if (integrationData?.connectedIntegrations?.includes(integrationName)) {
      integrationOption = {
        value: integrationName,
        label: integrationName,
        status: 'connected',
        url: ''
      };
    } else if (
      integrationData?.notConnectedButAvailableIntegrations
        ?.map((integration) => integration?.toLowerCase())
        .includes(integrationName?.toLowerCase())
    ) {
      let route = '';

      Object.keys(integrationsCardData).map((key) => {
        if (integrationsCardData?.[key].title === integrationName) {
          route = integrationName?.toLowerCase()?.replace(/\s/g, '');
        }
      });

      integrationOption = {
        value: integrationName,
        label: integrationName,
        status: 'not-connected-available',
        url: `${AppConstants.baseURL}integrations/${route}`
      };
    } else {
      integrationOption = {
        value: integrationName,
        label: integrationName,
        status: 'not-connected-unavailable',
        url: ''
      };
    }

    const list = isAtsList ? atsList : hrmsList;
    if (!list?.includes(integrationName)) {
      integrationOption = {
        value: integrationName,
        label: integrationName,
        status: 'not-connected-unavailable',
        url: ''
      };
    }

    options.push(integrationOption);
  });

  return options;
};

/**
 * Handles the validation of a company URL.
 *
 * @param {Event} e - The event object.
 * @param {Function} setCompanyUrl - The function to set the company URL.
 * @param {Function} setCompanyUrlError - The function to set the company URL error.
 */
export const handleCompanyUrlValidation = async (e, setCompanyUrl, setCompanyUrlError) => {
  let trimmedValue = getTrimmedValue(e.target.value, false);

  if (trimmedValue === '-') {
    setCompanyUrl(trimmedValue);
    setCompanyUrlError(null);
    return;
  }

  setCompanyUrl(trimmedValue);

  const urlPattern =
    /^(https?:\/\/)?((www\.)?[a-zA-Z\d-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?)(:\d{1,5})?(\/[\w\-\.~:\/\?#\[\]@!$&'()*+,;=]*)?$/;

  setCompanyUrlError(
    trimmedValue !== '-' && !urlPattern.test(trimmedValue)
      ? 'Please provide a valid company URL'
      : null
  );
};

export const handleApostrophe = (value) => {
  if (value) {
    return value.replace(/'\w/g, (match) => {
      return match[0] + match[1].toLowerCase();
    });
  }
  return '';
};
